'use client';

import { forwardRef, useEffect, useRef, useState, type CSSProperties } from 'react';
import { SField, type SFieldSize, type SFieldAddonAlign, type SFieldStatus } from '../SField';
import { SIcon, type SIconName } from '../SIcon';
import { type SColor } from '../../lib/color';
import { cn } from '../../lib/cn';
import { AUTOFILL_RESET_CLASS } from '../../lib/autofill';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { type STooltipProps } from '../STooltip';

export type SNumberInputSize = SFieldSize;

const PAD: Record<
  SNumberInputSize,
  { fontSize: number; lineHeight: number; stepper: number; icon: number }
> = {
  sm: { fontSize: 12, lineHeight: 20, stepper: 20, icon: 12 },
  md: { fontSize: 14, lineHeight: 24, stepper: 24, icon: 14 },
};

export interface SNumberInputProps {
  /** 입력 값 */
  value?: string | number | null;
  /** 값 변경 (sdUpdate) — number|null 전달 */
  onValueChange?: (value: number | null) => void;
  /** 크기 */
  size?: SNumberInputSize;
  /** 최솟값 */
  min?: number;
  /** 최댓값 */
  max?: number;
  /** 스텝 단위 */
  step?: number;
  /** 증감 버튼 표시 */
  useButton?: boolean;
  /** 소수점 입력 허용 */
  useDecimal?: boolean;
  /** 소수점 이하 최대 자리수 */
  maxDecimalLength?: number;
  /** 입력 앞 접두 텍스트 */
  inputPrefix?: string;
  /** 입력 뒤 접미 텍스트 */
  inputSuffix?: string;
  /** 네이티브 input className */
  inputClass?: string;
  /** 네이티브 input style */
  inputStyle?: CSSProperties;
  /** 마운트 시 자동 포커스 */
  autoFocus?: boolean;
  /** 포커스 이벤트 (sdFocus) */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** 블러 이벤트 (sdBlur) */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** 폼 연동 name */
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** 유효성 규칙 — blur 시 검증 */
  rules?: Rule[];
  status?: SFieldStatus;
  focused?: boolean;
  hovered?: boolean;
  // ── SField 래퍼 ──
  label?: string;
  labelWidth?: number | string;
  icon?: SIconName;
  iconColor?: SColor;
  labelTooltip?: string;
  labelTooltipProps?: Partial<STooltipProps>;
  addonLabel?: string;
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  width?: number | string;
  className?: string;
  style?: CSSProperties;
}

const toNumber = (val: unknown): number | null => {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return isNaN(val) ? null : val;
  const cleaned = String(val).replace(/,/g, '').trim();
  if (!/^-?(\d+\.?\d*|\d*\.\d+)$/.test(cleaned)) return null;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};

const formatWithCommas = (value: number | null): string => {
  if (value === null || value === undefined || isNaN(value)) return '';
  const isNeg = value < 0;
  const [intPart, decPart] = Math.abs(value).toString().split('.');
  const formatted = (+intPart).toLocaleString();
  const result = isNeg ? '-' + formatted : formatted;
  return decPart !== undefined && decPart !== '' ? result + '.' + decPart : result;
};

const addCommas = (value: string): string => value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const formatInputWithCommas = (value: string): string => {
  const isNeg = value.startsWith('-');
  const unsigned = isNeg ? value.slice(1) : value;
  const hasDecimal = unsigned.includes('.');
  const [intPart = '', decPart = ''] = unsigned.split('.');
  const formattedInt = addCommas(intPart === '' ? '0' : intPart);

  return `${isNeg ? '-' : ''}${formattedInt}${hasDecimal ? `.${decPart}` : ''}`;
};

const getDecimalLimit = (useDecimal: boolean, maxDecimalLength?: number): number | undefined => {
  if (!useDecimal || maxDecimalLength === undefined || !Number.isFinite(maxDecimalLength)) {
    return undefined;
  }
  return Math.max(0, Math.floor(maxDecimalLength));
};

const applyDecimalLimit = (value: number | null, decimalLimit?: number): number | null => {
  if (value === null || decimalLimit === undefined) return value;
  return Number(value.toFixed(decimalLimit));
};

const getLogicalCaret = (value: string, caret: number | null): number => {
  return value.slice(0, caret ?? value.length).replace(/,/g, '').length;
};

const getDisplayCaret = (value: string, logicalCaret: number): number => {
  if (logicalCaret <= 0) return 0;

  let seen = 0;
  for (let i = 0; i < value.length; i += 1) {
    if (value[i] === ',') continue;
    seen += 1;
    if (seen >= logicalCaret) return i + 1;
  }

  return value.length;
};

/** SNumberInput — sd-number-input 포팅. 콤마 포맷 + min/max/step + 옵션 스테퍼. */
export const SNumberInput = forwardRef<HTMLInputElement, SNumberInputProps>(function SNumberInput(
  {
    value,
    onValueChange,
    size = 'sm',
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    step = 1,
    useButton = false,
    useDecimal = false,
    maxDecimalLength,
    inputPrefix = '',
    inputSuffix = '',
    inputClass,
    inputStyle,
    autoFocus,
    onFocus,
    onBlur,
    name,
    placeholder = '입력해 주세요.',
    disabled = false,
    readOnly = false,
    rules,
    status,
    focused,
    hovered,
    label,
    labelWidth,
    icon,
    iconColor,
    labelTooltip,
    labelTooltipProps,
    addonLabel,
    addonAlign,
    hint,
    error,
    errorMessage,
    width,
    className,
    style,
  },
  ref,
) {
  const p = PAD[size];
  const padX = `var(--cmp-numberInput-${size}-paddingX)`;
  const decimalLimit = getDecimalLimit(useDecimal, maxDecimalLength);
  const numValue = applyDecimalLimit(toNumber(value), decimalLimit);
  const [display, setDisplay] = useState(() => formatWithCommas(numValue));
  const [ruleError, setRuleError] = useState('');
  const innerRef = useRef<HTMLInputElement | null>(null);
  const setRefs = (el: HTMLInputElement | null) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
  };

  // value prop 변경 시 표시값 동기화
  useEffect(() => {
    if (document.activeElement === innerRef.current) return;
    setDisplay(formatWithCommas(applyDecimalLimit(toNumber(value), decimalLimit)));
  }, [decimalLimit, value]);

  // SForm 등록 — submit 시 폼이 이 필드를 일괄 검증한다 (폼 밖이면 no-op)
  useFormField({
    name,
    validate: () => {
      const msg = runRules(numValue, rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  const resolvedError = error || ruleError !== '';
  const resolvedStatus =
    status ?? (rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined);

  const restoreCaret = (nextDisplay: string, logicalCaret?: number) => {
    const input = innerRef.current;
    if (logicalCaret === undefined || !input || document.activeElement !== input) return;

    const nextCaret = getDisplayCaret(nextDisplay, logicalCaret);
    requestAnimationFrame(() => {
      if (document.activeElement !== input) return;
      input.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const setDisplayWithCaret = (nextDisplay: string, logicalCaret?: number) => {
    setDisplay(nextDisplay);
    restoreCaret(nextDisplay, logicalCaret);
  };

  const resetRejectedInput = (target: HTMLInputElement, logicalCaret: number) => {
    target.value = display;
    restoreCaret(display, Math.min(logicalCaret, display.replace(/,/g, '').length));
  };

  const emit = (n: number | null, logicalCaret?: number) => {
    const next = applyDecimalLimit(n, decimalLimit);
    const nextDisplay = formatWithCommas(next);
    setDisplayWithCaret(nextDisplay, logicalCaret);
    onValueChange?.(next);
  };

  const incDisabled = disabled || readOnly || (numValue !== null && numValue >= max);
  const decDisabled = disabled || readOnly || (numValue !== null && numValue <= min);

  const increment = () => {
    if (incDisabled) return;
    const cur = numValue ?? (min === Number.NEGATIVE_INFINITY ? 0 : min);
    const next = applyDecimalLimit(Math.min(cur + step, max), decimalLimit);
    if (next !== cur) emit(next);
  };
  const decrement = () => {
    if (decDisabled) return;
    const cur = numValue ?? (min === Number.NEGATIVE_INFINITY ? 0 : min);
    const next = applyDecimalLimit(Math.max(cur - step, min), decimalLimit);
    if (next !== cur) emit(next);
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    const raw = target.value;
    const logicalCaret = getLogicalCaret(raw, target.selectionStart);

    if (raw === '') {
      setDisplayWithCaret('', 0);
      onValueChange?.(null);
      return;
    }
    const cleaned = raw.replace(/,/g, '');
    const decimals = (cleaned.match(/\./g) || []).length;
    if (decimals > 1) {
      resetRejectedInput(target, logicalCaret);
      return;
    }
    if (cleaned === '-' || cleaned === '.' || cleaned === '-.') {
      setDisplayWithCaret(cleaned, logicalCaret);
      return;
    }
    if (!useDecimal && cleaned.includes('.')) {
      resetRejectedInput(target, logicalCaret);
      return;
    }
    if (decimalLimit !== undefined && cleaned.includes('.')) {
      const fraction = cleaned.split('.')[1] ?? '';
      if (fraction.length > decimalLimit) {
        resetRejectedInput(target, logicalCaret);
        return;
      }
    }
    // 입력 도중 '123.' 유지
    if (cleaned.endsWith('.') && decimals === 1) {
      const parsed = toNumber(cleaned.slice(0, -1));
      if (parsed !== null) {
        setDisplayWithCaret(formatInputWithCommas(cleaned), logicalCaret);
        return;
      }
    }
    const parsed = toNumber(cleaned);
    if (parsed === null) {
      resetRejectedInput(target, logicalCaret);
      return;
    }
    // 타이핑 도중에는 범위 밖 값도 허용한다(예: min=10 에서 '50' 을 향해 '5' 입력).
    // 최종 클램프는 blur 시점에 수행한다 — 여기서 막으면 유효한 중간 입력이 삼켜진다.
    setDisplayWithCaret(formatInputWithCommas(cleaned), logicalCaret);
    onValueChange?.(applyDecimalLimit(parsed, decimalLimit));
  };

  const stepper = (dir: 'dec' | 'inc') => {
    const off = dir === 'inc' ? incDisabled : decDisabled;
    return (
      <button
        type="button"
        tabIndex={-1}
        disabled={off}
        aria-label={dir === 'inc' ? '증가' : '감소'}
        onClick={dir === 'inc' ? increment : decrement}
        className={cn(
          'flex flex-shrink-0 items-center justify-center border border-solid border-transparent transition-colors',
          off
            ? 'cursor-not-allowed'
            : 'cursor-pointer hover:border-[var(--color-border-accentLight)]',
        )}
        style={{
          width: `var(--cmp-numberInput-stepper-${size}-size)`,
          height: `var(--cmp-numberInput-stepper-${size}-size)`,
          borderRadius: 'var(--cmp-numberInput-stepper-radius)',
          background: off
            ? 'var(--cmp-numberInput-stepper-bg-disabled)'
            : 'var(--cmp-numberInput-stepper-bg-default)',
        }}
      >
        <SIcon
          name={(dir === 'inc' ? 'add' : 'minus') as SIconName}
          size={p.icon}
          color={
            off
              ? 'var(--cmp-numberInput-stepper-icon-disabled)'
              : 'var(--cmp-numberInput-stepper-icon-default)'
          }
        />
      </button>
    );
  };

  const affixColor = disabled
    ? 'var(--cmp-numberInput-text-disabled)'
    : 'var(--cmp-numberInput-text-placeholder)';

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      icon={icon}
      iconColor={iconColor}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      addonLabel={addonLabel}
      addonAlign={addonAlign}
      size={size}
      hint={hint}
      error={resolvedError}
      status={resolvedStatus}
      focused={focused}
      hovered={hovered}
      errorMessage={ruleError !== '' ? ruleError : errorMessage}
      width={width}
      disabled={disabled}
      readOnly={readOnly}
      className={className}
      style={style}
    >
      <div
        className="flex h-full w-full items-center"
        // 폰트는 컨테이너에 지정해 input·prefix·suffix 가 동일한 메트릭을 상속 (SInput 패턴)
        style={{
          // 원본 sd-number-input: content gap = padding-x 토큰 (gap 토큰 아님)
          gap: `var(--cmp-numberInput-${size}-paddingX)`,
          padding: `0 ${useButton ? padX : `calc(${padX} * 2)`}`,
          fontSize: p.fontSize,
          lineHeight: `${p.lineHeight}px`,
        }}
      >
        {useButton && stepper('dec')}
        {inputPrefix !== '' && (
          <span className="flex-shrink-0" style={{ color: affixColor }}>
            {inputPrefix}
          </span>
        )}
        <input
          ref={setRefs}
          name={name}
          type="text"
          inputMode={useDecimal ? 'decimal' : 'numeric'}
          value={display}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          onChange={handleInput}
          onKeyDown={e => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              increment();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              decrement();
            }
          }}
          onFocus={e => onFocus?.(e)}
          onBlur={e => {
            // blur 시 [min, max] 범위로 클램프. 값이 바뀌면 emit(display+onValueChange 갱신),
            // 그대로면 표시값만 재포맷한다.
            const cleaned = display.replace(/,/g, '');
            const n = toNumber(cleaned);
            const clamped = n === null ? null : Math.min(Math.max(n, min), max);
            if (clamped !== n) emit(clamped);
            else setDisplay(n === null ? '' : formatInputWithCommas(cleaned));
            if (rules && rules.length > 0) setRuleError(runRules(clamped, rules));
            onBlur?.(e);
          }}
          className={cn(
            // h-full 대신 라인박스 높이 그대로 두어 prefix/suffix 와 동일하게 flex 로 수직 센터링
            // (h-full 이면 input 내부 텍스트 센터링을 브라우저가 수행해 1px 어긋날 수 있음)
            'min-w-0 flex-1 border-0 bg-transparent p-0 outline-none placeholder:text-[color:var(--cmp-numberInput-text-placeholder)] disabled:cursor-not-allowed',
            AUTOFILL_RESET_CLASS,
            inputClass,
          )}
          style={{
            // fontSize/lineHeight 는 컨테이너에서 상속
            textAlign: useButton ? 'center' : 'right',
            color: disabled
              ? 'var(--cmp-numberInput-text-disabled)'
              : 'var(--cmp-numberInput-text-default)',
            ...inputStyle,
          }}
        />
        {inputSuffix !== '' && (
          <span className="flex-shrink-0" style={{ color: affixColor }}>
            {inputSuffix}
          </span>
        )}
        {useButton && stepper('inc')}
      </div>
    </SField>
  );
});
